/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// components
import HearroPatientDataSidebar from '../../../components/hearro/layout/patientDetailPage/HearroPatientDataSidebar';
import HearroPatientReport from '../../../components/hearro/layout/patientDetailPage/HearroPatientReport';
import CommonModal from '../../../components/modal/CommonModal';

// api
import {
	getPatientFromHearro,
	getPatientInfoFromHearro,
	getPatientProfileImgFromHearro,
	putPatientToHearro,
} from '../../../services/apis/hearro/patient';

// NOTE(rollback): 아래 mock 데이터는 환자 상세 화면에서 표시하는
// `스크리닝 no. / Random no. / Baseline 방문일` 값만 보정(supplement)하기 위해 사용합니다.
// DB 스키마/API에서 해당 값들이 제공되기 시작하면 이 import + 보정 로직을 통째로 제거하면 됩니다.
import MOCK_PATIENT_DATA from '../../../data/hearroMockPatientData';

function HearroPatientManageDetailPage() {
	const { pid } = useParams();
	const [patientData, setPatientData] = useState({});
	const [patientProfileImg, setPatientProfileImg] = useState(null);
	const [resolvedPid, setResolvedPid] = useState('');

	// NOTE(rollback): DB 환자 데이터에 mock의 3개 필드(스크리닝/랜덤/베이스라인)만 주입하는 보정 함수
	// 매칭 우선순위: displayName이 mock.name과 일치 -> displayName이 mock.initial과 일치
	function applyMockSupplementIfMatched(dbPatient) {
		try {
			const displayName = (dbPatient?.displayName ?? '').toString().trim();
			if (!displayName) return dbPatient;

			const matched = (MOCK_PATIENT_DATA || []).find(m => {
				const mockName = (m?.name ?? '').toString().trim();
				const mockInitial = (m?.initial ?? '').toString().trim();
				return displayName === mockName || displayName === mockInitial;
			});

			if (!matched) return dbPatient;

			return {
				...dbPatient,
				// NOTE: 화면에서 사용하는 키 그대로 주입합니다. (HearroPatientDataSidebar 참고)
				// NOTE: displayName(실제 이름)은 수정 모달에서 사용될 수 있어서 유지하고,
				// 상세 화면 표시용 이니셜은 별도 필드(displayInitial)로 제공합니다.
				displayInitial: matched.initial,
				screeningNumber: matched.screeningNumber,
				randomNumber: matched.randomNumber,
				baselineVisitDate: matched.baselineVisitDate,
			};
		} catch (e) {
			console.error(e);
			return dbPatient;
		}
	}

	// 환자 수정 modal
	const [showModifyPatientModal, setShowModifyPatientModal] = useState(false);
	const [patientDisplayName, setPatientDisplayName] = useState('');
	const [patientPhone1, setPatientPhone1] = useState('');
	const [patientPhone2, setPatientPhone2] = useState('');
	const [patientPhone3, setPatientPhone3] = useState('');
	const [patientBirthYear, setPatientBirthYear] = useState(new Date().getFullYear());
	const [patientBirthMonth, setPatientBirthMonth] = useState(new Date().getMonth() + 1);
	const [patientBirthDay, setPatientBirthDay] = useState(new Date().getDate());
	const [patientDoctorCode, setPatientDoctorCode] = useState('');
	const [patientGender, setPatientGender] = useState('');
	const [patientSerialNum, setPatientSerialNum] = useState('');
	const [patientHearAidGetYear, setPatientHearAidGetYear] = useState(new Date().getFullYear());
	const [patientHearAidGetMonth, setPatientHearAidGetMonth] = useState(new Date().getMonth() + 1);
	const [patientHearAidGetDay, setPatientHearAidGetDay] = useState(new Date().getDate());
	const [patientHearAidFitYear, setPatientHearAidFitYear] = useState(new Date().getFullYear());
	const [patientHearAidFitMonth, setPatientHearAidFitMonth] = useState(new Date().getMonth() + 1);
	const [patientHearAidFitDay, setPatientHearAidFitDay] = useState(new Date().getDate());

	// 환자 수정 모달 state 초기화
	function initializeModifyPatientInfo(patientInfo) {
		try {
			setPatientDisplayName(patientInfo.displayName || '');
		} catch (err) {
			console.error(err);
			setPatientDisplayName('');
		}
		try {
			const newPhone1 = patientInfo.phoneNumber.substring(0, 3);
			const newPhone2 = patientInfo.phoneNumber.substring(3, patientInfo.phoneNumber.length - 4);
			const newPhone3 = patientInfo.phoneNumber.substring(
				patientInfo.phoneNumber.length - 4,
				patientInfo.phoneNumber.length,
			);
			setPatientPhone1(!Number.isNaN(newPhone1) ? newPhone1 : '');
			setPatientPhone2(!Number.isNaN(newPhone2) ? newPhone2 : '');
			setPatientPhone3(!Number.isNaN(newPhone3) ? newPhone3 : '');
		} catch (err) {
			console.error(err);
			setPatientPhone1('');
			setPatientPhone2('');
			setPatientPhone3('');
		}
		try {
			const newYear = parseInt(patientInfo.birth.substring(0, 4), 10);
			const newMonth = parseInt(patientInfo.birth.substr(4, 2), 10);
			const newDay = parseInt(patientInfo.birth.substr(6, 2), 10);
			setPatientBirthYear(!Number.isNaN(newYear) ? newYear : new Date().getFullYear());
			setPatientBirthMonth(!Number.isNaN(newMonth) ? newMonth : new Date().getMonth() + 1);
			setPatientBirthDay(!Number.isNaN(newDay) ? newDay : new Date().getDate());
		} catch (err) {
			console.error(err);
			setPatientBirthYear(new Date().getFullYear());
			setPatientBirthMonth(new Date().getMonth() + 1);
			setPatientBirthDay(new Date().getDate());
		}
		try {
			setPatientDoctorCode(patientInfo.psctCode || '');
		} catch (err) {
			console.error(err);
			setPatientDoctorCode('');
		}
		try {
			setPatientGender(patientInfo.sex || '');
		} catch (err) {
			console.error(err);
			setPatientGender('');
		}
		try {
			setPatientSerialNum(patientInfo.serialNum || '');
		} catch (err) {
			console.error(err);
			setPatientSerialNum('');
		}
		// 보청기 수령일
		try {
			if (!patientInfo.hearAidGetDate || patientInfo.hearAidGetDate.length < 8) {
				throw new Error('Invalid hearAidGetDate format');
			}
			const newYear = parseInt(patientInfo.hearAidGetDate.substring(0, 4), 10);
			const newMonth = parseInt(patientInfo.hearAidGetDate.substr(4, 2), 10);
			const newDay = parseInt(patientInfo.hearAidGetDate.substr(6, 2), 10);
			setPatientHearAidGetYear(!Number.isNaN(newYear) ? newYear : new Date().getFullYear());
			setPatientHearAidGetMonth(!Number.isNaN(newMonth) ? newMonth : new Date().getMonth() + 1);
			setPatientHearAidGetDay(!Number.isNaN(newDay) ? newDay : new Date().getDate());
		} catch (err) {
			console.error(err);
			setPatientHearAidGetYear(new Date().getFullYear());
			setPatientHearAidGetMonth(new Date().getMonth() + 1);
			setPatientHearAidGetDay(new Date().getDate());
		}
		// 보청기 최근 피팅일
		try {
			if (!patientInfo.hearAidFitDate || patientInfo.hearAidFitDate.length < 8) {
				throw new Error('Invalid hearAidFitDate format');
			}
			const newYear = parseInt(patientInfo.hearAidFitDate.substring(0, 4), 10);
			const newMonth = parseInt(patientInfo.hearAidFitDate.substr(4, 2), 10);
			const newDay = parseInt(patientInfo.hearAidFitDate.substr(6, 2), 10);
			setPatientHearAidFitYear(!Number.isNaN(newYear) ? newYear : new Date().getFullYear());
			setPatientHearAidFitMonth(!Number.isNaN(newMonth) ? newMonth : new Date().getMonth() + 1);
			setPatientHearAidFitDay(!Number.isNaN(newDay) ? newDay : new Date().getDate());
		} catch (err) {
			console.error(err);
			setPatientHearAidFitYear(new Date().getFullYear());
			setPatientHearAidFitMonth(new Date().getMonth() + 1);
			setPatientHearAidFitDay(new Date().getDate());
		}
	}

	async function getPatientDataFromAPI(targetPidOrName) {
		try {
			const key = (targetPidOrName ?? pid ?? '').toString();
			if (!key) {
				setPatientData({});
				setResolvedPid('');
				return;
			}

			const resp = await getPatientInfoFromHearro({ pid: key });
			if (parseInt(resp.status / 200, 10) === 1) {
				const nextData = applyMockSupplementIfMatched(resp.data);
				setPatientData(nextData);
				setResolvedPid(nextData?.pid || resp.data?.pid || key);
				initializeModifyPatientInfo(nextData);
				return;
			}
			throw new Error();
		} catch (err) {
			try {
				const key = (targetPidOrName ?? pid ?? '').toString();
				const res = await getPatientFromHearro({ searchtext: key, page: 1, order: 'display_name' });
				if (parseInt(res.status / 200, 10) === 1) {
					const list = res.data || [];
					const found = list.find(p => p.displayName === key);
					if (found?.pid) {
						const resp2 = await getPatientInfoFromHearro({ pid: found.pid });
						if (parseInt(resp2.status / 200, 10) === 1) {
							const nextData = applyMockSupplementIfMatched(resp2.data);
							setPatientData(nextData);
							setResolvedPid(nextData?.pid || resp2.data?.pid || found.pid);
							initializeModifyPatientInfo(nextData);
							return;
						}
					}
				}
			} catch (fallbackErr) {
				console.error(fallbackErr);
			}
			console.error(err);
			setPatientData({});
			setResolvedPid('');
		}
	}

	async function getPatientProfileImgFromAPI(targetPidOrName) {
		try {
			const key = (targetPidOrName ?? pid ?? '').toString();
			if (!key) {
				setPatientProfileImg(null);
				return;
			}
			const res = await getPatientProfileImgFromHearro({ pid: key });
			if (parseInt(res.status / 200, 10) === 1) {
				const imgURL = URL.createObjectURL(res.data);
				setPatientProfileImg(imgURL || '');
				return;
			}
			throw new Error();
		} catch (err) {
			console.error(err);
			setPatientProfileImg(null);
		}
	}

	function handleShowModifyPatientModal() {
		setShowModifyPatientModal(true);
	}

	function handleCloseModifyPatientModal() {
		setShowModifyPatientModal(false);
		initializeModifyPatientInfo(patientData);
	}

	async function modifyPatientToAPI() {
		try {
			const hearAidGetDateStr =
				patientHearAidGetYear.toString() +
				patientHearAidGetMonth.toString().padStart(2, 0) +
				patientHearAidGetDay.toString().padStart(2, 0);
			const hearAidFitDateStr =
				patientHearAidFitYear.toString() +
				patientHearAidFitMonth.toString().padStart(2, 0) +
				patientHearAidFitDay.toString().padStart(2, 0);
			const queryStringRequest = {
				pid: patientData.pid || pid,
				displayName: patientDisplayName,
				birth:
					patientBirthYear.toString() +
					patientBirthMonth.toString().padStart(2, 0) +
					patientBirthDay.toString().padStart(2, 0),
				sex: patientGender,
				serialNum: patientSerialNum,
				hearAidGetDate: hearAidGetDateStr,
				hearAidFitDate: hearAidFitDateStr,
				phoneNumber: patientPhone1 + patientPhone2 + patientPhone3,
			};
			const resp = await putPatientToHearro(queryStringRequest);
			if (parseInt(resp.status / 200, 10) === 1) {
				handleCloseModifyPatientModal();
				await getPatientDataFromAPI(patientData.pid || pid);
				await getPatientProfileImgFromAPI(patientData.pid || pid);
				return;
			}
			console.error(`응답 실패 : ${JSON.stringify(resp)}`);
			throw new Error();
		} catch (err) {
			console.error(err);
			alert('환자 수정에 실패하였습니다.');
		}
	}

	const selectYear = () => {
		const yearItems = [];
		for (let year = 1900; year <= new Date().getFullYear(); year++) {
			yearItems.push(
				<option key={year} value={year}>
					{year}
				</option>,
			);
		}
		return yearItems;
	};
	const selectMonth = () => {
		const monthItems = [];
		for (let month = 1; month <= 12; month++) {
			monthItems.push(
				<option key={month} value={month}>
					{month}
				</option>,
			);
		}
		return monthItems;
	};
	const selectDay = () => {
		const dayItems = [];
		for (let day = 1; day <= 31; day++) {
			dayItems.push(
				<option key={day} value={day}>
					{day}
				</option>,
			);
		}
		return dayItems;
	};

	const modifyPatientModalBody = (
		<div className="modalbody">
			<div className="formModalItem">
				<div className="formModalItem__label">이름</div>
				<div className="formModalItem__input">
					<input
						type="text"
						className="formModalItem__input__plainText"
						placeholder="이름"
						value={patientDisplayName}
						onChange={e => setPatientDisplayName(e.target.value)}
						maxLength={30}
					/>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">휴대폰 번호</div>
				<div className="formModalItem__input">
					<input
						type="text"
						className="formModalItem__input__phoneNumber"
						value={patientPhone1}
						onChange={e => {
							setPatientPhone1(e.target.value);
						}}
						size={3}
						maxLength={3}
					/>
					<span className="phoneDivider">-</span>
					<input
						type="text"
						className="formModalItem__input__phoneNumber"
						value={patientPhone2}
						onChange={e => {
							setPatientPhone2(e.target.value);
						}}
						size={4}
						maxLength={4}
					/>
					<span className="phoneDivider">-</span>
					<input
						type="text"
						className="formModalItem__input__phoneNumber"
						value={patientPhone3}
						onChange={e => {
							setPatientPhone3(e.target.value);
						}}
						size={4}
						maxLength={4}
					/>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">생년월일</div>
				<div className="formModalItem__input">
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientBirthYear(e.target.selectedOptions[0].value)}
						value={patientBirthYear}
					>
						{selectYear()}
					</select>
					<span className="birthText">년</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientBirthMonth(e.target.selectedOptions[0].value)}
						value={patientBirthMonth}
					>
						{selectMonth()}
					</select>
					<span className="birthText">월</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientBirthDay(e.target.selectedOptions[0].value)}
						value={patientBirthDay}
					>
						{selectDay()}
					</select>
					<span className="birthText">일</span>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">처방 코드</div>
				<div className="formModalItem__input">
					<input
						type="text"
						className="formModalItem__input__plainTextWithButton"
						placeholder="처방 코드"
						value={patientDoctorCode}
						onChange={e => setPatientDoctorCode(e.target.value)}
						maxLength={30}
						disabled
					/>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">성별</div>
				<div className="formModalItem__input">
					<div className="formModalItem__input__radio">
						<input
							type="radio"
							id="male"
							value="M"
							name="gender"
							checked={patientGender === 'M'}
							onChange={e => setPatientGender(e.target.value)}
						/>{' '}
						<label htmlFor="male" className="formModalItem__input__radioLabel">
							남성
						</label>
					</div>
					<div className="formModalItem__input__radio">
						<input
							type="radio"
							id="female"
							value="F"
							name="gender"
							checked={patientGender === 'F'}
							onChange={e => setPatientGender(e.target.value)}
						/>{' '}
						<label htmlFor="female" className="formModalItem__input__radioLabel">
							여성
						</label>
					</div>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">
					보청기
					<br />
					시리얼 넘버
				</div>
				<div className="formModalItem__input">
					<input
						type="text"
						className="formModalItem__input__plainTextWithButton"
						placeholder="시리얼 넘버"
						value={patientSerialNum}
						onChange={e => setPatientSerialNum(e.target.value)}
					/>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">
					보청기
					<br />
					수령일
				</div>
				<div className="formModalItem__input">
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientHearAidGetYear(e.target.selectedOptions[0].value)}
						value={patientHearAidGetYear}
					>
						{selectYear()}
					</select>
					<span className="birthText">년</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientHearAidGetMonth(e.target.selectedOptions[0].value)}
						value={patientHearAidGetMonth}
					>
						{selectMonth()}
					</select>
					<span className="birthText">월</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientHearAidGetDay(e.target.selectedOptions[0].value)}
						value={patientHearAidGetDay}
					>
						{selectDay()}
					</select>
					<span className="birthText">일</span>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">
					보청기
					<br />
					최근 피팅일
				</div>
				<div className="formModalItem__input">
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientHearAidFitYear(e.target.selectedOptions[0].value)}
						value={patientHearAidFitYear}
					>
						{selectYear()}
					</select>
					<span className="birthText">년</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientHearAidFitMonth(e.target.selectedOptions[0].value)}
						value={patientHearAidFitMonth}
					>
						{selectMonth()}
					</select>
					<span className="birthText">월</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setPatientHearAidFitDay(e.target.selectedOptions[0].value)}
						value={patientHearAidFitDay}
					>
						{selectDay()}
					</select>
					<span className="birthText">일</span>
				</div>
			</div>
		</div>
	);
	const modifyPatientModalFooter = (
		<div className="modalfooter">
			<button className="submitButton__rounded" onClick={modifyPatientToAPI}>
				저장
			</button>
			<button className="cancelButton__rounded" onClick={handleCloseModifyPatientModal}>
				취소
			</button>
		</div>
	);

	useEffect(() => {
		getPatientDataFromAPI(pid);
	}, [pid]);

	useEffect(() => {
		getPatientProfileImgFromAPI(resolvedPid);
	}, [resolvedPid]);

	return (
		<div className="dashboardContent">
			<div className="dashboardContent__title">
				<span className="dashboardContent__title__content">환자 관리 {'>'} 환자 상세 페이지</span>
			</div>
			<div className="dashboardContent__inner">
				<div className="patientManageDetail">
					<HearroPatientDataSidebar
						patientData={patientData}
						patientProfileImg={patientProfileImg}
						handlePatientModify={handleShowModifyPatientModal}
					/>
					<HearroPatientReport />
				</div>
			</div>
			<CommonModal
				header="환자 수정"
				body={modifyPatientModalBody}
				footer={modifyPatientModalFooter}
				show={showModifyPatientModal}
				setClose={handleCloseModifyPatientModal}
				size="lg"
			/>
		</div>
	);
}

export default HearroPatientManageDetailPage;
